// ─────────────────────────────────────────────────────────────────────────────
// Nodes page — Global Network Map.
//   This component only wires things together:
//     • useNodesData   — fetches the node list + KPI summary, owns fetch errors
//     • useNodeFilters — search / status filter / sort → visible rows
//     • local state    — view mode (list|map), selected node, Add Node wizard
//   All rendering lives in ./components; NetworkCard owns the map/list swap.
//
// Failure handling has two layers, and they catch different things:
//   • useNodesData + nodesService absorb everything the NETWORK can do — a dead
//     server, a 500, an expired session — and turn it into a message the page
//     renders next to (or instead of) the data. No status code or server string
//     ever reaches the screen.
//   • PageErrorBoundary catches what RENDERING can do. An unexpected payload
//     shape that throws mid-render would otherwise blank the whole app; here it
//     degrades to a retry panel and leaves the shell intact.
//
// RBAC: reaching this page already requires node_monitoring access (RequireModule
// + sidebar), which is READ. The CRUD controls need read_write on top of that, so
// `canWrite` gates Add Node and the Edit/Delete row entries — they render only for
// Admin + Security Admin, mirroring the backend's own gate. A viewer without it
// sees the fleet, the map and node details, and no action that would 403.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useCallback } from "react";
import styles from "./Nodes.module.css";
import { useNodesData } from "./hooks/useNodesData";
import { useNodeFilters } from "./hooks/useNodeFilters";
import { usePermissions, MODULES } from "../../features/auth/model/usePermissions";
import StatsRow from "./components/StatsRow";
import NetworkCard from "./components/NetworkCard";
import NodeDetailModal from "./components/NodeDetailModal";
import AddNodeWizard from "./components/AddNodeWizard";
import PageErrorBoundary from "./components/PageErrorBoundary";

export default function Nodes() {
  const { nodes, summary, loading, error, summaryError, hasLoaded, refetch } = useNodesData();
  const filters = useNodeFilters(nodes);
  const { canWrite } = usePermissions();
  const mayWrite = canWrite(MODULES.NODE_MONITORING);

  // The fleet failing is the more serious of the two, so it wins the banner.
  // A summary-only failure still gets said out loud rather than leaving the KPI
  // tiles dashed with no explanation.
  const notice = error ?? summaryError;

  const [viewMode, setViewMode] = useState("list"); // "list" | "map"
  const [selected, setSelected] = useState(null);
  const [adding, setAdding]     = useState(false);  // Add Node wizard

  // Clicking the open node again closes the modal.
  const handleSelect = useCallback((node) => {
    setSelected((cur) => (cur?.node_id === node.node_id ? null : node));
  }, []);

  // A create/update/delete landed. Refetch so the table and the KPI tiles reflect
  // it — a write can change geo, close sessions or remove a row outright, so the
  // cached list is stale immediately. The modal that triggered this stays open to
  // report what the server did; only the data behind it is refreshed.
  const handleChanged = useCallback(() => {
    refetch();
    // Drop a detail modal pinned to a node that may no longer exist, or whose
    // fields the write just rewrote.
    setSelected(null);
  }, [refetch]);

  return (
    <div className={styles.page}>
      <PageErrorBoundary onRetry={refetch}>
        <StatsRow summary={summary} nodes={nodes} loading={loading} />

        <NetworkCard
          view={viewMode}
          onView={setViewMode}
          filters={filters}
          loading={loading}
          error={error}
          hasLoaded={hasLoaded}
          notice={notice}
          selectedId={selected?.node_id ?? null}
          onSelect={handleSelect}
          onRefresh={refetch}
          onAddNode={() => setAdding(true)}
          onChanged={handleChanged}
          canWrite={mayWrite}
        />

        {selected && (
          <NodeDetailModal node={selected} onClose={() => setSelected(null)} />
        )}

        {adding && (
          <AddNodeWizard
            onClose={() => setAdding(false)}
            onCreated={handleChanged}
          />
        )}
      </PageErrorBoundary>
    </div>
  );
}
